import _ from "lodash";
import queryString from "query-string";
import Constants from "./constants";
import { REVIEWS_IMAGE_KEY } from "./productConstants";
import { getVariantData, getMetafield } from "./shop";

const { DEVICE_OS_MAP } = Constants;

/**
 * When setting defaultProps for props that are objects with multiple properties,
 * if you choose to only override one of those properties, you override all other default props
 * in that prop object.
 *
 * mergeDefaultObjectProps allows you to take the original default props object and the child props
 * and merge the two together so you don't lose the default prop values in that object
 * @param {object} origProps
 * @param {object} newProps
 */
export const mergeDefaultObjectProps = (origProps, newProps) => {
  return _.reduce(
    newProps,
    (mergedProps, val, key) => {
      mergedProps[key] = _.merge({}, origProps, val);

      return mergedProps;
    },
    {}
  );
};

export const moveItemInArray = (arr, from, to) => {
  arr.splice(to, 0, arr.splice(from, 1)[0]);

  return arr;
};

// callAll - Calls all the functions it’s given, if they exist
// Taken from https://blog.kentcdodds.com/how-to-give-rendering-control-to-users-with-prop-getters-549eaef76acf
export const callAll =
  (...fns) =>
  (...args) =>
    fns.forEach((fn) => fn && fn(...args));

/**
 * Taken from 'downshift' package render props + props getter pattern
 * Takes an argument and if it's an array, returns the first item in the array
 * otherwise returns the argument
 * @param {*} arg the maybe-array
 * @param {*} defaultValue the value if arg is falsey not defined
 * @return {*} the arg or it's first item
 */
export const unwrapArray = (arg, defaultValue) => {
  const a = Array.isArray(arg) ? arg[0] : arg;
  if (!a && defaultValue) {
    return defaultValue;
  } else {
    return a;
  }
};

export const priceConstructor = ({ price, showDollarSign = true }) =>
  showDollarSign ? `$${price}` : price;

export const slugify = ({
  path,
  delimiter = "-",
  exclude = Constants.UNSAFE_URL_CHARACTERS,
  urlQuery = {},
}) => {
  if (!path) {
    throw new Error('"path" is required');
  }

  let newPath = decodeURIComponent(path)
    .toLowerCase()
    .replace(/\s+/g, delimiter);
  let query = "";

  if (!_.isEmpty(urlQuery)) {
    query = `?${queryString.stringify(urlQuery)}`;
  }

  if (exclude.test(newPath)) {
    newPath = newPath.replace(exclude, "");
  }

  return {
    pathname: newPath,
    query,
  };
};

// Append query params to URL that may or may not have pre-existing
// query params baked in.
export const constructUrlWithQueryParams = (url, query) => {
  if (!url) {
    return console.warn('constructUrlWithQueryParams(): "url" is required.');
  }

  if (!query) {
    return url;
  }

  // Trim off leading "?"
  const q = query.startsWith("?") ? query.slice(1) : query;

  // If query params already exist in "url", append query string
  // with "&"
  if (url.includes("?")) {
    return `${url}&${q}`;
  }

  // Else, just append query string with "?"
  return `${url}?${q}`;
};

export const isMobileDevice = () => {
  if (window) {
    const { userAgent } = window.navigator;

    return (
      userAgent.includes("iPhone") ||
      userAgent.includes("iPhone") ||
      userAgent.includes("iPad") ||
      userAgent.includes("Android") ||
      userAgent.includes("iPhone")
    );
  }

  return false;
};

export const getDeviceBrowser = (
  userAgent,
  query = null // Sort of a hack for Android Webview since we can't depend on UA
) => {
  const getDeviceAndOS = (userAgent) => {
    const iPhone = userAgent.includes("iPhone");
    const iPad = userAgent.includes("iPad");

    if (iPhone || iPad) {
      const iOS = DEVICE_OS_MAP.iOS;

      if (iPhone) return window.screen.height > 736 ? iOS.iPhoneX : iOS.iPhone;
      if (iPad) return iOS.iPad;
    }

    if (userAgent.includes("Android")) return DEVICE_OS_MAP.android;

    if (userAgent.includes("Macintosh")) return DEVICE_OS_MAP.macOS;

    if (userAgent.includes("Windows")) return DEVICE_OS_MAP.windows;

    return DEVICE_OS_MAP.default;
  };
  const getViewType = (deviceType, UA = window.navigator.userAgent) => {
    const view = deviceType.view;
    const isChrome = UA.includes("Chrome");
    const isSafari = UA.includes("Safari") && !isChrome;

    if (!view) return { name: "" };

    if (isChrome) return view.chrome;

    if (isSafari) return view.safari;

    if (UA.includes("Firefox")) return view.firefox;

    return view.default;
  };
  const constructClassName = (deviceType, viewType) =>
    deviceType.className && viewType.className
      ? `${deviceType.className} ${viewType.className}`
      : "";

  const deviceType = getDeviceAndOS(userAgent);
  const viewType = getViewType(deviceType, userAgent);

  return {
    className: constructClassName(deviceType, viewType),
    deviceType: `${deviceType.name}`,
    view: getViewType(deviceType).name,
  };
};

export const togglePageScrollability = (preventPageScroll) => {
  if (preventPageScroll) {
    return document.body.classList.add("no_scroll");
  }

  if (document.body.classList.contains("no_scroll")) {
    return document.body.classList.remove("no_scroll");
  }
};

export const formatTrackingNameValue = (val = null) => {
  // Check if val is a string before trying to replace underscores
  if (_.isString(val)) {
    return !val ? null : val.replace(/_/gi, " ");
  }

  // If val is not a string, just return val
  return val;
};

export const stripUnderscoresFromObject = (obj = {}) => {
  if (!_.isObject(obj)) {
    return console.error('"obj" must be an object');
  }

  return _.reduce(
    obj,
    (res, val, key) => {
      res[formatTrackingNameValue(key)] = formatTrackingNameValue(val);

      return res;
    },
    {}
  );
};

//
export const constructReviewsPropsFromShopifyData = (productShopifyData) => {
  const { price, product } = getVariantData(productShopifyData);

  // With our Yotpo accounts linked to Shopify, we need to link to the product ID
  // provided in the Shopify data in order to show reviews within the product group
  const yotpoProductId = window
    .atob(product.id)
    .split("gid://shopify/Product/")[1];

  return {
    productId: yotpoProductId,
    name: productShopifyData.title,
    url: `${process.env.REACT_APP_WEB_URL}/${productShopifyData.handle}`,
    imageUrl: getMetafield(productShopifyData, REVIEWS_IMAGE_KEY), // Get path from metafield
    price,
  };
};

export const dispatchActionCreator = (dispatch, action) => (params) =>
  dispatch(action(params));

export const isNewCartEnabled = () => process.env.REACT_APP_NEW_CART === "true";

export const sleepStoreIsEnabled = () =>
  process.env.REACT_APP_ENABLE_SLEEP_STORE === "true";

export const builderIsEnabled = () =>
  process.env.REACT_APP_ENABLE_BUILDER_IO === "true";

export const getLongFormDate = (dateString) => {
  let date;
  if (dateString) {
    date = new Date(dateString);
  } else {
    date = new Date();
  }
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

export const blogIsEnabled = () =>
  process.env.REACT_APP_BLOG_ENABLED === "true";

const general = {
  constructUrlWithQueryParams,
  mergeDefaultObjectProps,
  moveItemInArray,
  callAll,
  unwrapArray,
  priceConstructor,
  getDeviceBrowser,
  togglePageScrollability,
  constructReviewsPropsFromShopifyData,
  dispatchActionCreator,
  isNewCartEnabled,
  sleepStoreIsEnabled,
  // Detail Page URL constructors
  formatTrackingNameValue,
  stripUnderscoresFromObject,
  getLongFormDate,
  blogIsEnabled,
};

export default general;
